/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement } from 'react';
import type { ShopperProducts } from '@/scapi';
import ImageGallery from '@/components/image-gallery';
import ProductInfo from './product-info';
import ProductAccordion from './product-accordion';
import WhatsIncluded from './whats-included';
import ShopOnAmazon from './shop-on-amazon';
import ProductCartActions from '@/components/product-cart-actions';
import ProductViewProvider from '@/providers/product-view';
import { useProductGalleryMedia } from '@/hooks/product/use-product-gallery-media';
import { isProductSet, isProductBundle } from '@/lib/product/product-utils';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { UITarget } from '@/targets/ui-target';

interface ProductViewProps {
    product: ShopperProducts.schemas['Product'];
    mode?: 'add' | 'edit';
}

/**
 * ProductView component renders a complete product detail view with image gallery and product information.
 *
 * @param props - The component props
 * @param props.product - The product data from Salesforce Commerce Cloud containing all product details,
 *                        variants, pricing, and metadata
 *
 * @returns A React element containing the complete product view layout
 *
 * @example
 * ```tsx
 * <ProductView product={productData} />
 * ```
 */
export default function ProductView({ product }: ProductViewProps): ReactElement {
    const config = useConfig();
    // Calculate directly without useMemo since these are simple operations
    const isProductASet = isProductSet(product);
    const isProductABundle = isProductBundle(product);

    // PDP gallery media comes from the product record's Widen fields (pdpMainImage / pdpAltImage1…25 /
    // videoLink), not the internal SFCC imageGroups / DIS pipeline.
    const { media } = useProductGalleryMedia(product);

    return (
        <ProductViewProvider product={product} mode="add">
            {/* Figma proportions: gallery narrower than the info column (~9:10), 56px gap at lg. */}
            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,9fr)_minmax(0,10fr)] gap-4 lg:gap-14">
                {/* Left Column - Image Gallery. Full-bleed to the window edges on mobile (negates the
                    section-container padding); constrained within the grid at lg. */}
                <div className="order-1 -mx-4 sm:-mx-8 lg:mx-0">
                    <ImageGallery
                        key={product.id}
                        images={media}
                        eager={!isProductASet && !isProductABundle}
                        fixedThumbnails
                        productName={product.name}
                    />
                    <UITarget targetId="sfcc.pdp.agent.productHelper" />
                </div>

                {/* Right Column - Product Info */}
                <div className="order-2 space-y-6">
                    {/* Wishlist/share action icons hidden on the PDP for now (may return) via hideActionIcons.
                        Mobile shows the Amazon CTA below the price (belowPriceSlot); desktop shows it after
                        What's Included (below). Each instance is visibility-toggled by breakpoint. */}
                    <ProductInfo
                        product={product}
                        hideActionIcons
                        belowPriceSlot={<ShopOnAmazon product={product} className="my-2 w-full lg:hidden" />}
                    />
                    <WhatsIncluded product={product} />
                    <ShopOnAmazon product={product} className="my-4 hidden lg:flex" />
                    {/* Cart actions stay gated behind checkout (catalog-only hides them); rendering
                        only when enabled avoids an empty wrapper adding phantom spacing. Resurrected later. */}
                    {config.features.checkout.enabled && <ProductCartActions product={product} />}
                    <UITarget targetId="sfcc.pdp.returnsWarranty" />
                    <UITarget targetId="sfcc.pdp.faq" />
                    <UITarget targetId="sfcc.pdp.collapsibles" />
                    <ProductAccordion product={product} />
                </div>
            </div>
        </ProductViewProvider>
    );
}
